import { useState } from "react";
import { motion } from "framer-motion";
import { 
  User, 
  Mail, 
  Lock, 
  Phone,
  ArrowLeft,
  Eye,
  EyeOff
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientAuth = () => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    phone: ""
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const endpoint = isLogin ? "/client/login" : "/client/register";
      const payload = isLogin 
        ? { email: formData.email, password: formData.password }
        : formData;
      
      const response = await axios.post(`${API}${endpoint}`, payload);
      
      // Store token and client info
      localStorage.setItem("client_token", response.data.access_token);
      localStorage.setItem("client_info", JSON.stringify(response.data.client));
      
      toast.success(isLogin ? "Welcome back!" : "Account created successfully!");
      navigate("/portal");
    } catch (error) {
      const message = error.response?.data?.detail || "Something went wrong";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-6" data-testid="client-auth-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/">
            <img 
              src="https://customer-assets.emergentagent.com/job_property-hub-465/artifacts/aqf0sdon_AIP1%20No%20Background.png" 
              alt="AIP Logo" 
              className="h-16 w-auto mx-auto mb-6"
            />
          </Link>
          <h1 className="text-2xl font-bold mb-2">
            {isLogin ? "Client Portal Login" : "Create Account"}
          </h1>
          <p className="text-neutral-400">
            {isLogin 
              ? "Access your reports, bookings & invoices" 
              : "Register to access your client portal"}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-neutral-900/50 border border-neutral-800 rounded-2xl p-8">
          <div className="space-y-4">
            {!isLogin && (
              <div>
                <Label className="text-neutral-400 text-sm">Full Name</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" size={18} />
                  <Input 
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="bg-neutral-950 border-neutral-800 pl-10 h-12"
                    placeholder="John Smith"
                    required={!isLogin}
                    data-testid="client-name-input"
                  />
                </div>
              </div>
            )}

            <div>
              <Label className="text-neutral-400 text-sm">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" size={18} />
                <Input 
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="bg-neutral-950 border-neutral-800 pl-10 h-12"
                  placeholder="you@example.com"
                  required
                  data-testid="client-email-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-neutral-400 text-sm">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" size={18} />
                <Input 
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  className="bg-neutral-950 border-neutral-800 pl-10 pr-10 h-12"
                  placeholder="••••••••"
                  required
                  minLength={6}
                  data-testid="client-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-white"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {!isLogin && (
              <div>
                <Label className="text-neutral-400 text-sm">Phone (Optional)</Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" size={18} />
                  <Input 
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="bg-neutral-950 border-neutral-800 pl-10 h-12"
                    placeholder="+44 7841 238 061"
                    data-testid="client-phone-input"
                  />
                </div>
              </div>
            )}
          </div>

          <Button 
            type="submit"
            className="btn-primary rounded-full w-full py-6 mt-6"
            disabled={isLoading}
            data-testid="client-auth-submit"
          >
            {isLoading 
              ? "Please wait..." 
              : isLogin ? "Sign In" : "Create Account"}
          </Button>

          <div className="text-center mt-6">
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-neutral-400 hover:text-white text-sm"
            >
              {isLogin 
                ? "Don't have an account? Register" 
                : "Already have an account? Sign In"}
            </button>
          </div>
        </form>

        <div className="text-center mt-6">
          <Link to="/" className="text-neutral-500 hover:text-white text-sm inline-flex items-center gap-2">
            <ArrowLeft size={14} />
            Back to Home
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default ClientAuth;
